function collectionSpinner() {
  if (window.themeVariables.settings.pageType !== 'collection') return;

  const container = document.querySelector('.shopify-section--main-collection');
  if (!container) return;

  document.addEventListener("theme:loading:start", function() {
    container.classList.add('collection-loading');
  });

  document.addEventListener("theme:loading:end", function() {
    container.classList.remove('collection-loading');
  });
}

window.addEventListener('load', function() {
  collectionSpinner();
});


/* =========================================
   URL NORMALISATION
   Ensures Klaviyo/email clients receive
   complete HTTPS URLs instead of // or /
========================================= */

function absoluteUrl(url) {
  if (!url) return '';

  // Already absolute
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  // Shopify CDN URLs such as //cdn.shopify.com/...
  if (url.startsWith('//')) {
    return 'https:' + url;
  }

  // Store-relative URLs such as /products/product-name
  if (url.startsWith('/')) {
    return window.location.origin + url;
  }

  return url;
}


window.formatMoney = function(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const value = parseInt(cents, 10);
  if (isNaN(value)) return '';

  const moneyFormat = format || window.themeVariables.settings.moneyFormat;

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (isNaN(number) || number === null) return 0;

    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const integer = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const fraction = parts[1] || '';

    return fraction ? `${integer}${decimal}${fraction}` : integer;
  }

  return moneyFormat
    .replace(/\{\{\s*amount\s*\}\}/, formatWithDelimiters(value, 2))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, formatWithDelimiters(value, 0))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, formatWithDelimiters(value, 2, '.', ','))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, formatWithDelimiters(value, 0, '.', ','))
    .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, formatWithDelimiters(value, 2, "'", '.'));
};


window.convertToUSD = async function(rawAmount, currency) {
  const actualAmount = rawAmount / 100;
  const from = currency.toLowerCase();

  if (from === "usd") return actualAmount;

  const CACHE_KEY = `exchange_rate_${from}_usd`;

  let rate = null;
  const cached = sessionStorage.getItem(CACHE_KEY);

  if (cached) {
    rate = parseFloat(cached);
  }

  if (!rate) {
    const urls = [
      `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${from}.json`,
      `https://latest.currency-api.pages.dev/v1/currencies/${from}.json`,
    ];

    let data;

    for (const url of urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue;

        data = await res.json();
        break;
      } catch {
        continue;
      }
    }

    if (!data) {
      throw new Error(`Failed to fetch exchange rate for ${currency}`);
    }

    rate = data[from]?.usd;

    if (!rate) {
      throw new Error(`No USD rate found for ${currency}`);
    }

    sessionStorage.setItem(CACHE_KEY, rate.toString());
  }

  return Math.round(actualAmount * rate * 100);
};


window.convertFromUSD = async function(rawAmount, currency) {
  const actualAmount = rawAmount / 100;
  const to = currency.toLowerCase();

  if (to === "usd") return rawAmount;

  const CACHE_KEY = `exchange_rate_usd_${to}`;

  let rate = null;
  const cached = sessionStorage.getItem(CACHE_KEY);

  if (cached) {
    rate = parseFloat(cached);
  }

  if (!rate) {
    const urls = [
      `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json`,
      `https://latest.currency-api.pages.dev/v1/currencies/usd.json`,
    ];

    let data;

    for (const url of urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue;

        data = await res.json();
        break;
      } catch {
        continue;
      }
    }

    if (!data) {
      throw new Error(`Failed to fetch exchange rate for USD -> ${currency}`);
    }

    rate = data['usd']?.[to];

    if (!rate) {
      throw new Error(`No rate found for USD -> ${currency}`);
    }

    sessionStorage.setItem(CACHE_KEY, rate.toString());
  }

  return Math.round(actualAmount * rate * 100);
};


class ShippingBar extends HTMLElement {
  connectedCallback() {
    this._threshold = parseInt(this.getAttribute('threshold') || '0', 10);
    this._totalPrice = parseInt(this.getAttribute('total-price') || '0', 10);
    this._reachedMsg = this.getAttribute('reached-message') || '';
    this._unreachedMsg = this.getAttribute('unreached-message') || '';
    this._convert = this.getAttribute('convert') === 'true';
    this._currency = this.getAttribute('currency') || '';

    this._buildDOM();
    this._update(this._totalPrice);

    document.addEventListener('cart:change', (event) => {
      const newTotal = event?.detail?.cart?.total_price;

      if (typeof newTotal === 'number') {
        this._update(newTotal);
      }
    });
  }

  _buildDOM() {
    this.innerHTML = `
      <span class="free-shipping-bar__message text-subdued"></span>
      <div class="free-shipping-bar__track" role="progressbar" aria-valuemin="0" aria-valuemax="100">
        <div class="free-shipping-bar__progress"></div>
      </div>
    `;

    this._messageEl = this.querySelector('.free-shipping-bar__message');
    this._progressEl = this.querySelector('.free-shipping-bar__progress');
    this._trackEl = this.querySelector('.free-shipping-bar__track');
  }

  async _validateThreshold(totalPrice) {
    const totalPriceUSD = await convertToUSD(totalPrice, this._currency);
    return totalPriceUSD >= this._threshold;
  }

  async _update(totalPrice) {
    if (!this._messageEl || !this._progressEl) return;

    const threshold = this._threshold;

    let totalPriceUSD = totalPrice;

    if (this._convert) {
      totalPriceUSD = await convertToUSD(totalPrice, this._currency);
    }

    const reached = threshold === 0 || totalPriceUSD >= threshold;

    const pct = threshold === 0
      ? 100
      : Math.min(100, Math.round((totalPriceUSD / threshold) * 100));

    this.classList.toggle('free-shipping-bar--reached', reached);

    this._progressEl.style.width = pct + '%';
    this._trackEl.setAttribute('aria-valuenow', pct);

    if (reached) {
      this._messageEl.innerHTML = this._reachedMsg;
    } else {
      const remainingUSD = threshold - totalPriceUSD;

      let remaining = remainingUSD;

      if (this._convert) {
        remaining = await convertFromUSD(remainingUSD, this._currency);
      }

      const formatted = window.formatMoney(remaining);

      this._messageEl.innerHTML = this._unreachedMsg.replace(
        /{{ remaining_amount }}/g,
        formatted
      );
    }
  }
}


if (!customElements.get('shipping-bar')) {
  customElements.define('shipping-bar', ShippingBar);
}


/* =========================================
   COLLECTION BACK TO TOP
   Appears after the second rendered product row
========================================= */

if (!customElements.get('collection-back-to-top')) {
  class CollectionBackToTop extends HTMLElement {
    connectedCallback() {
      this.productList = document.querySelector(
        this.getAttribute('product-list-selector')
      );
      this.button = this.querySelector('button');

      if (!this.productList || !this.button) return;

      this.updateVisibility = this.updateVisibility.bind(this);
      this.recalculateTrigger = this.recalculateTrigger.bind(this);

      this.button.addEventListener('click', () => {
        window.scrollTo({
          top: 0,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth'
        });
      });

      window.addEventListener('scroll', this.updateVisibility, { passive: true });
      window.addEventListener('resize', this.recalculateTrigger);
      document.addEventListener('theme:loading:end', this.recalculateTrigger);

      this.resizeObserver = new ResizeObserver(this.recalculateTrigger);
      this.resizeObserver.observe(this.productList);

      this.recalculateTrigger();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.updateVisibility);
      window.removeEventListener('resize', this.recalculateTrigger);
      document.removeEventListener('theme:loading:end', this.recalculateTrigger);
      this.resizeObserver?.disconnect();
    }

    recalculateTrigger() {
      const cards = Array.from(
        this.productList.querySelectorAll(':scope > .product-card')
      ).filter(card => card.offsetParent !== null);

      const rowTops = [];

      for (const card of cards) {
        if (!rowTops.some(top => Math.abs(top - card.offsetTop) < 2)) {
          rowTops.push(card.offsetTop);
        }
      }

      rowTops.sort((a, b) => a - b);

      if (rowTops.length < 3) {
        this.triggerPoint = Infinity;
      } else {
        const productListTop =
          this.productList.getBoundingClientRect().top + window.scrollY;
        this.triggerPoint = productListTop + rowTops[2];
      }

      this.updateVisibility();
    }

    updateVisibility() {
      const shouldShow = window.scrollY >= this.triggerPoint;
      this.toggleAttribute('hidden', !shouldShow);
    }
  }

  customElements.define('collection-back-to-top', CollectionBackToTop);
}


/* =========================================
   KLAVIYO VIEWED PRODUCT STYLING
========================================= */

document.addEventListener('DOMContentLoaded', async () => {
  const stylingDataElement = document.getElementById(
    'salt-murphy-styling-data'
  );

  if (!stylingDataElement) return;

  try {
    const stylingData = JSON.parse(stylingDataElement.textContent);
    const productId = stylingData.productId;

    const response = await fetch(
      `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=4&intent=related`
    );

    if (!response.ok) return;

    const data = await response.json();

    const relatedProducts = data.products || [];


    /* -------------------------------------
       COMPLETE THE LOOK
       Keep max 2 available products
    ------------------------------------- */

    const matchingProducts = (stylingData.matchingProducts || [])
      .filter(product => product.available)
      .slice(0, 2)
      .map(product => ({
        ...product,
        image: absoluteUrl(product.image),
        url: absoluteUrl(product.url)
      }));


    const finalRecommendations = [...matchingProducts];


    /* -------------------------------------
       SHOPIFY RELATED
       Fill remaining slots until max 2
    ------------------------------------- */

    for (const product of relatedProducts) {
      if (finalRecommendations.length >= 2) break;

      const alreadyIncluded = finalRecommendations.some(
        item => String(item.id) === String(product.id)
      );

      if (!alreadyIncluded && product.available) {
        finalRecommendations.push({
          id: product.id,
          title: product.title,

          // IMPORTANT: full HTTPS URL
          url: absoluteUrl(product.url),

          // IMPORTANT: full HTTPS image URL
          image: absoluteUrl(product.featured_image),

          price: (product.price / 100).toFixed(2),
          available: product.available
        });
      }
    }


    stylingData.relatedProducts = relatedProducts;
    stylingData.finalRecommendations = finalRecommendations;

    window.saltMurphyStylingData = stylingData;


    /* -------------------------------------
       SEND EVENT TO KLAVIYO
    ------------------------------------- */

    if (stylingData.finalRecommendations.length > 0) {
      window.klaviyo = window.klaviyo || [];

      window.klaviyo.push([
        'track',
        'Viewed Product Styling',
        {
          ProductID: stylingData.productId,
          Name: stylingData.productTitle,

          // Full product URL
          URL: absoluteUrl(stylingData.productUrl),

          // Full HTTPS image URL
          ImageURL: absoluteUrl(stylingData.productImage),

          Price: stylingData.productPrice,

          StylingRecommendations: stylingData.finalRecommendations
        }
      ]);
    }


    console.log(
      'Salt Murphy styling data:',
      window.saltMurphyStylingData
    );

  } catch (error) {
    console.error(
      'Salt Murphy styling data error:',
      error
    );
  }
});
